<template>
    <input ref="inputRef" :id="_id" v-model="input" :name="name" :type="type" :required="_required" :aria-required="_required ? 'true' : 'false'"
        :class="[sizeClass, {invalid: isInvalid}]" v-bind="ariaValidationAttributes" v-on="events" v-focus="autofocus"/>
</template>

<script setup lang="ts">
    import {useFormInput} from '@/composables/formInput';
    import {usePortuna} from '@/composables/portuna';
    import {FormRowKey} from '@/library/form';
    import {ref, inject, toRefs} from 'vue';

    const portuna = usePortuna();

    const emit = defineEmits<{
        (eventName: 'update:modelValue', modelValue: Optional<InputValueType>): void;
    }>();

    const props = withDefaults(
        defineProps<{
            id?: string;
            name?: string;
            required?: boolean;
            overwriteRequired?: boolean;
            errorName?: string;
            filterName?: string;
            type?: string;
            modelValue?: InputValueType;
            context?: Record<string, any>;
            size?: string;
            events?: object;
            autofocus?: boolean;
        }>(),
        {
            modelValue: '',
            context: () => ({}),
            events: () => ({}),
            required: false,
            overwriteRequired: false,
            errorName: '',
            filterName: '',
            type: 'text',
            size: 'long',
            autofocus: false,
        },
    );

    const parentFormRow = inject<Nullable<IFormRowContext>>(FormRowKey, null);

    const inputRef = ref<HTMLInputElement>();

    const {
              id,
              name,
              required,
              overwriteRequired,
              errorName,
              filterName,
              type,
              modelValue,
              context,
              size,
              events,
              autofocus,
          } = toRefs(props);

    const {
              expose,
              input,
              sizeClass,
              _id,
              _required,
              isInvalid,
              ariaValidationAttributes,
          } = useFormInput(
        parentFormRow,
        id,
        required,
        overwriteRequired,
        errorName,
        filterName,
        size,
        modelValue,
        context,
        inputRef,
        emit,
    );

    defineExpose(Object.assign({}, portuna, expose, {
        componentName: 'FormInput',
    }));
</script>